import * as React from 'react';

import { cn, TouchSelect, TouchTextField } from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';

import {
  $deleteEntry,
  $itemsInterfaceDisabled,
  $itemsInterfaceFacilityDefaultUnit,
  $itemsInterfaceInventoryLocked,
  $itemsInterfacePricingLocked,
  $itemsInterfaceShowPricing,
  $itemsInterfaceWeightUnitMode,
  $updateLineItem,
} from '../_state';
import { NormalizedLineItem } from '../_types';
import {
  CatalyticSizeField,
  DeleteButtonField,
  EditItemButtonField,
  EstimatedPriceField,
  GrossWeightField,
  UnitPriceField,
} from '../fields';
import { CatalyticConverterItemField } from '../fields/CatalyticConverterItemField';
import {
  getItemRowGridCols,
  ITEM_ROW_BORDER,
  ITEM_ROW_ERROR_BORDER,
  ITEM_ROW_FOCUS_WITHIN,
} from '../layout';
import { ItemRow } from '../layout/ItemRow';

import {
  $openCatalyticDetails,
  $openVehicleDetails,
} from '~/modules/trade/_state/materialsInterface.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { enumComplianceOptions } from '~/modules/trade/types/scaleTickets';
import { useUIPreferences } from '~/shared/hooks';

type ItemRowCatalyticConverterProps = {
  lineItem: NormalizedLineItem;
  autoFocus?: boolean;
  onFocusRow?: (id: string) => void;
  hasError?: boolean;
};

//
// ⚛️ ⚛️ ⚛️ ITEM ROW CATALYTIC CONVERTER ⚛️ ⚛️ ⚛️
//
export const ItemRowCatalyticConverter: React.FC<
  ItemRowCatalyticConverterProps
> = ({ lineItem, autoFocus = false, onFocusRow, hasError }) => {
  // Translations
  const t = useTranslations();
  const catDetails = t.scaleTicketPro.catalyticConverterDetails;

  // Refs
  const grossWeightRef = React.useRef<HTMLDivElement>(null);
  const inventorySelectRef = React.useRef<HTMLDivElement>(null);

  // Atoms
  const disabled = useAtomValue($itemsInterfaceDisabled);
  const inventoryLocked = useAtomValue($itemsInterfaceInventoryLocked);
  const pricingLocked = useAtomValue($itemsInterfacePricingLocked);
  const showPricing = useAtomValue($itemsInterfaceShowPricing);
  const weightUnit = useAtomValue($itemsInterfaceWeightUnitMode);
  const facilityDefaultUnit = useAtomValue($itemsInterfaceFacilityDefaultUnit);
  const onUpdateItem = useSetAtom($updateLineItem);
  const onDeleteItem = useSetAtom($deleteEntry);
  const openVehicleDetails = useSetAtom($openVehicleDetails);
  const openCatalyticDetails = useSetAtom($openCatalyticDetails);

  const { touchModeEnabled } = useUIPreferences();

  // Memoized values
  const complianceOptions = React.useMemo(
    () =>
      Object.values(enumComplianceOptions).map((option) => ({
        key: option,
        label: catDetails.complianceOptions[option],
      })),
    [catDetails.complianceOptions],
  );

  // Callbacks
  const handleSerialChange = React.useCallback(
    (value: string) =>
      onUpdateItem(lineItem.id, { catSerialNumber: value || null }),
    [lineItem.id, onUpdateItem],
  );

  const handleOemChange = React.useCallback(
    (value: string) => onUpdateItem(lineItem.id, { catOemCode: value || null }),
    [lineItem.id, onUpdateItem],
  );

  const handleComplianceChange = React.useCallback(
    (value: string) =>
      onUpdateItem(lineItem.id, { catComplianceStatus: value || null }),
    [lineItem.id, onUpdateItem],
  );

  const handleDetailRowPress = React.useCallback(() => {
    openCatalyticDetails({
      lineItemId: lineItem.id,
      itemId: lineItem.itemId,
      serialNumber: lineItem.catSerialNumber,
      oemCode: lineItem.catOemCode,
      compliance:
        lineItem.catComplianceStatus ?? enumComplianceOptions.documented,
    });
  }, [
    lineItem.id,
    lineItem.itemId,
    lineItem.catSerialNumber,
    lineItem.catOemCode,
    lineItem.catComplianceStatus,
    openCatalyticDetails,
  ]);

  const handleClickDetailRow = React.useCallback(
    (e: React.SyntheticEvent<HTMLDivElement>) => {
      handleDetailRowPress();
      e.stopPropagation();
    },
    [handleDetailRowPress],
  );

  const handleEditButtonPress = React.useCallback(() => {
    openVehicleDetails({
      lineItemId: lineItem.id,
      itemId: lineItem.itemId,
      vin: lineItem.vehicleVin,
      make: lineItem.vehicleMake,
      model: lineItem.vehicleModel,
      year: lineItem.vehicleYear,
    });
  }, [
    lineItem.id,
    lineItem.itemId,
    lineItem.vehicleVin,
    lineItem.vehicleMake,
    lineItem.vehicleModel,
    lineItem.vehicleYear,
    openVehicleDetails,
  ]);

  // Effects
  /** Focuses the appropriate input element when this row receives auto-focus */
  React.useEffect(() => {
    if (!autoFocus) return;

    const targetRef = touchModeEnabled ? grossWeightRef : inventorySelectRef;
    const inputElement = targetRef?.current?.querySelector('input');
    if (inputElement) {
      setTimeout(() => {
        inputElement.focus();
        inputElement.select();
      }, 100);
    }
  }, [autoFocus, touchModeEnabled]);

  const inventoryFieldsDisabled = disabled || inventoryLocked;
  const pricingFieldsDisabled = disabled || pricingLocked;

  const baseFieldProps = {
    lineItem,
    onUpdateItem,
    onDeleteItem,
    weightUnit,
    facilityDefaultUnit,
  };

  const inventoryFieldProps = {
    ...baseFieldProps,
    disabled: inventoryFieldsDisabled,
  };

  const pricingFieldProps = {
    ...baseFieldProps,
    disabled: pricingFieldsDisabled,
  };

  const gridClassName = getItemRowGridCols(showPricing);

  const detailRowOnPress = touchModeEnabled ? handleDetailRowPress : undefined;

  return (
    <div className={cn('@ItemRowCatalyticConverter')}>
      <ItemRow
        gridTemplate={gridClassName}
        className={cn(
          '@ItemRowCatalyticConverter relative',
          ITEM_ROW_FOCUS_WITHIN,
          hasError && ITEM_ROW_ERROR_BORDER,
        )}
        onFocus={() => onFocusRow?.(lineItem.id)}
      >
        <div ref={inventorySelectRef}>
          <CatalyticConverterItemField
            {...inventoryFieldProps}
            lineItem={lineItem}
          />
        </div>

        <div ref={grossWeightRef}>
          <GrossWeightField {...inventoryFieldProps} />
        </div>

        <CatalyticSizeField {...inventoryFieldProps} />
        <div />

        {showPricing && (
          <>
            <UnitPriceField {...pricingFieldProps} />
            <div />
            <EstimatedPriceField {...pricingFieldProps} />
          </>
        )}

        <EditItemButtonField
          {...inventoryFieldProps}
          onClick={handleEditButtonPress}
        />
        <div />
        <DeleteButtonField {...inventoryFieldProps} />
      </ItemRow>

      <div
        className={cn(
          '@ItemRowCatalyticConverterDetails relative',
          'grid auto-rows-[2.75rem] gap-3 py-2 transition-all',
          'after:absolute after:right-0 after:bottom-0 after:left-[40px] after:h-px after:bg-neutral/20',
          gridClassName,
          ITEM_ROW_BORDER,
          ITEM_ROW_FOCUS_WITHIN,
          detailRowOnPress && 'cursor-pointer',
        )}
        onClick={detailRowOnPress && handleClickDetailRow}
        onFocus={(e) => {
          e.stopPropagation();
          onFocusRow?.(`${lineItem.id}:details`);
        }}
      >
        <div className="pl-[40px]">
          <TouchTextField
            placeholder={catDetails.tabs.serialNumber}
            value={lineItem.catSerialNumber ?? ''}
            onChange={handleSerialChange}
            isDisabled={inventoryFieldsDisabled}
          />
        </div>

        <TouchTextField
          placeholder={catDetails.tabs.oemCode}
          value={lineItem.catOemCode ?? ''}
          onChange={handleOemChange}
          isDisabled={inventoryFieldsDisabled}
        />

        <TouchSelect
          placeholder={catDetails.tabs.compliance}
          value={lineItem.catComplianceStatus ?? undefined}
          onChange={handleComplianceChange}
          options={complianceOptions}
          isDisabled={inventoryFieldsDisabled}
        />

        <div />

        {showPricing && (
          <>
            <div />
            <div />
            <div />
          </>
        )}

        <div />
        <div />
        <div />
      </div>
    </div>
  );
};
